import React, { useState } from 'react';
import { useCart } from '../componants/CartContext'; 
import { useNavigate } from 'react-router-dom';
import { MapPin, CreditCard, Truck, ShieldCheck, ChevronRight, Check, Plus, X, Crosshair, User, Phone, Home, Briefcase, Edit2, Trash2 } from 'lucide-react';

// Address Interface
interface Address {
    id: number;
    name: string;
    type: string;
    phone: string;
    address: string;
    pincode: string;
    city: string;
    state: string;
}

const CheckoutPage: React.FC = () => {
    const { cart, clearCart } = useCart() as any;
    const navigate = useNavigate();
    
    // --- STATES ---
    const [selectedAddress, setSelectedAddress] = useState<number | null>(null);
    const [paymentMethod, setPaymentMethod] = useState('cod');
    const [isPlacingOrder, setIsPlacingOrder] = useState(false);
    
    // Address Modal States
    const [showAddressModal, setShowAddressModal] = useState(false);
    const [locationLoading, setLocationLoading] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null); // Track editing mode
    
    // New Address Form State
    const [newAddr, setNewAddr] = useState({
        name: '',
        phone: '',
        pincode: '',
        city: '',
        state: '',
        addressLine: '',
        type: 'HOME'
    });

    // Mock Addresses (Initial Data - EMPTY NOW)
    const [addresses, setAddresses] = useState<Address[]>([]);

    // --- CALCULATIONS ---
    const subtotal = cart.reduce((acc: number, item: any) => acc + (Number(item.price) || 0) * item.quantity, 0);
    const tax = subtotal * 0.18; 
    const shipping = subtotal > 5000 ? 0.00 : 150.00; 
    const total = subtotal + tax + shipping;

    // --- LOCATION LOGIC ---
    const handleCurrentLocation = () => {
        if (!navigator.geolocation) {
            alert("Geolocation is not supported by your browser");
            return;
        }
        setLocationLoading(true);
        navigator.geolocation.getCurrentPosition(async (position) => {
            const { latitude, longitude } = position.coords;
            try {
                const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}`);
                const data = await response.json();
                
                if (data && data.address) {
                    const addr = data.address;
                    setNewAddr(prev => ({
                        ...prev,
                        pincode: addr.postcode || '',
                        city: addr.city || addr.town || addr.village || '',
                        state: addr.state || '',
                        addressLine: [addr.road, addr.suburb, addr.neighbourhood].filter(Boolean).join(', ')
                    }));
                }
            } catch (error) {
                console.error("Location fetch failed", error);
            } finally {
                setLocationLoading(false);
            }
        });
    };

    // --- OPEN MODAL (ADD / EDIT) ---
    const openModal = (addressToEdit?: Address) => {
        if (addressToEdit) {
            setEditingId(addressToEdit.id);
            setNewAddr({
                name: addressToEdit.name,
                phone: addressToEdit.phone,
                pincode: addressToEdit.pincode,
                city: addressToEdit.city,
                state: addressToEdit.state,
                addressLine: addressToEdit.address,
                type: addressToEdit.type
            });
        } else {
            setEditingId(null);
            setNewAddr({ name: '', phone: '', pincode: '', city: '', state: '', addressLine: '', type: 'HOME' });
        }
        setShowAddressModal(true);
    };

    // --- SAVE ADDRESS (ADD / UPDATE) ---
    const handleSaveAddress = (e: React.FormEvent) => {
        e.preventDefault();
        
        if (editingId) {
            // Update Existing
            setAddresses(addresses.map(addr => addr.id === editingId ? {
                ...addr,
                name: newAddr.name,
                phone: newAddr.phone,
                type: newAddr.type,
                pincode: newAddr.pincode,
                city: newAddr.city,
                state: newAddr.state,
                address: newAddr.addressLine
            } : addr));
        } else {
            // Add New
            const newId = Date.now();
            const formattedAddress: Address = {
                id: newId,
                name: newAddr.name,
                phone: newAddr.phone,
                type: newAddr.type,
                pincode: newAddr.pincode,
                city: newAddr.city,
                state: newAddr.state,
                address: newAddr.addressLine
            };
            setAddresses([...addresses, formattedAddress]);
            setSelectedAddress(newId);
        }
        setShowAddressModal(false);
    };

    // --- DELETE ADDRESS ---
    const handleDeleteAddress = (id: number) => {
        if (window.confirm("Are you sure you want to delete this address?")) {
            setAddresses(addresses.filter(addr => addr.id !== id));
            if (selectedAddress === id) setSelectedAddress(null);
        }
    };

    // --- PLACE ORDER ---
    const handlePlaceOrder = () => {
        if (!selectedAddress) {
            alert("Please select a delivery address.");
            return;
        }
        setIsPlacingOrder(true);
        setTimeout(() => {
            clearCart();
            alert("Order Placed Successfully!");
            navigate('/'); 
        }, 2000);
    };

    if (cart.length === 0) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 p-4">
                <h2 className="text-2xl font-bold text-gray-800">Your Cart is Empty</h2>
                <button onClick={() => navigate('/')} className="mt-4 bg-teal-600 text-white px-6 py-2 rounded-full font-bold hover:bg-teal-700">Go Home</button>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 font-sans py-8 relative">
            <div className="max-w-[1200px] mx-auto px-4 md:px-6">
                
                {/* Header Steps */}
                <div className="flex items-center text-sm font-medium text-gray-500 mb-8 overflow-x-auto">
                    <span className="cursor-pointer hover:text-teal-600 whitespace-nowrap" onClick={() => navigate('/cart')}>Cart</span>
                    <ChevronRight className="w-4 h-4 mx-2" />
                    <span className="text-teal-600 font-bold whitespace-nowrap">Checkout</span>
                    <ChevronRight className="w-4 h-4 mx-2" />
                    <span className="whitespace-nowrap">Payment</span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    
                    {/* LEFT COLUMN */}
                    <div className="lg:col-span-2 space-y-6">
                        
                        {/* 1. Delivery Address List */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <span className="bg-teal-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">1</span>
                                Delivery Address
                            </h2>
                            
                            <div className="space-y-4">
                                {addresses.length === 0 ? (
                                    <div className="text-center py-8 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50">
                                        <MapPin className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                                        <p className="text-gray-500 text-sm mb-4">No delivery address found.</p>
                                        <button 
                                            onClick={() => openModal()}
                                            className="bg-teal-600 text-white px-5 py-2 rounded-lg font-bold text-sm hover:bg-teal-700 shadow-sm inline-flex items-center gap-2"
                                        >
                                            <Plus className="w-4 h-4" /> Add New Address
                                        </button>
                                    </div>
                                ) : (
                                    <>
                                        {addresses.map((addr) => (
                                            <div key={addr.id} className={`relative flex items-start p-4 border rounded-xl transition-all ${selectedAddress === addr.id ? 'border-teal-600 bg-teal-50 ring-1 ring-teal-600' : 'border-gray-200 hover:border-teal-300'}`}>
                                                
                                                {/* Selection Click Area */}
                                                <div className="flex-1 flex cursor-pointer" onClick={() => setSelectedAddress(addr.id)}>
                                                    <div className="mt-1 mr-4">
                                                        {selectedAddress === addr.id ? 
                                                            <div className="w-5 h-5 bg-teal-600 rounded-full flex items-center justify-center shadow-sm"><Check className="w-3 h-3 text-white"/></div> :
                                                            <div className="w-5 h-5 border-2 border-gray-300 rounded-full"></div>
                                                        }
                                                    </div>
                                                    <div className="w-full">
                                                        <div className="flex flex-wrap items-center gap-2 mb-1">
                                                            <span className="font-bold text-gray-900 text-base">{addr.name}</span>
                                                            <span className={`text-[10px] font-bold px-2 py-0.5 rounded uppercase ${addr.type === 'HOME' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'}`}>
                                                                {addr.type}
                                                            </span>
                                                            <span className="text-sm font-semibold text-gray-500 ml-auto md:ml-2">{addr.phone}</span>
                                                        </div>
                                                        <p className="text-sm text-gray-600 leading-relaxed pr-16">
                                                            {addr.address}, {addr.city}, {addr.state} - <span className="font-bold text-gray-800">{addr.pincode}</span>
                                                        </p>
                                                        {selectedAddress === addr.id && (
                                                            <div className="mt-3">
                                                                <button className="bg-teal-600 text-white text-xs px-5 py-2 rounded-lg font-bold hover:bg-teal-700 shadow-md transition-all">DELIVER HERE</button>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>

                                                {/* Edit/Delete Actions */}
                                                <div className="flex flex-col gap-2 ml-2 border-l pl-3 border-gray-200">
                                                    <button onClick={() => openModal(addr)} className="text-blue-600 hover:bg-blue-50 p-1.5 rounded-full transition-colors" title="Edit">
                                                        <Edit2 className="w-4 h-4" />
                                                    </button>
                                                    <button onClick={() => handleDeleteAddress(addr.id)} className="text-red-500 hover:bg-red-50 p-1.5 rounded-full transition-colors" title="Delete">
                                                        <Trash2 className="w-4 h-4" />
                                                    </button>
                                                </div>

                                            </div>
                                        ))}
                                        
                                        <button 
                                            onClick={() => openModal()}
                                            className="text-teal-600 font-bold text-sm hover:underline pl-2 flex items-center gap-1 mt-4"
                                        >
                                            <Plus className="w-4 h-4" /> Add Another Address
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>

                        {/* 2. Payment Method */}
                        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
                            <h2 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                                <span className="bg-teal-600 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">2</span>
                                Payment Method
                            </h2>
                            <div className="space-y-3">
                                {/* Online */}
                                <label className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${paymentMethod === 'online' ? 'border-teal-600 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                    <input type="radio" name="payment" className="w-4 h-4 accent-teal-600" checked={paymentMethod === 'online'} onChange={() => setPaymentMethod('online')} />
                                    <div className="ml-4 flex-1">
                                        <span className="font-bold text-gray-900 block">UPI / Credit / Debit Card</span>
                                        <span className="text-xs text-green-600 font-medium bg-green-50 px-2 py-0.5 rounded inline-block mt-1">Extra 5% Discount applied</span>
                                    </div>
                                    <CreditCard className="w-6 h-6 text-gray-400" />
                                </label>
                                {/* COD */}
                                <label className={`flex items-center p-4 border rounded-xl cursor-pointer transition-all ${paymentMethod === 'cod' ? 'border-teal-600 bg-teal-50' : 'border-gray-200 hover:border-gray-300'}`}>
                                    <input type="radio" name="payment" className="w-4 h-4 accent-teal-600" checked={paymentMethod === 'cod'} onChange={() => setPaymentMethod('cod')} />
                                    <div className="ml-4 flex-1">
                                        <span className="font-bold text-gray-900 block">Cash on Delivery</span>
                                        <span className="text-xs text-gray-500">Pay cash at time of delivery</span>
                                    </div>
                                    <Truck className="w-6 h-6 text-gray-400" />
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* RIGHT COLUMN: Order Summary */}
                    <div className="h-fit space-y-6">
                        <div className="bg-white p-6 rounded-xl shadow-lg border border-teal-50 lg:sticky lg:top-24">
                            <h3 className="text-lg font-bold text-gray-800 mb-4 border-b pb-3">Price Details</h3>
                            <div className="space-y-3 text-sm text-gray-600 mb-6">
                                <div className="flex justify-between"><span>Price ({cart.length} items)</span><span className="font-medium">₹ {subtotal.toFixed(2)}</span></div>
                                <div className="flex justify-between"><span>Delivery Charges</span><span className="text-green-600 font-bold">{shipping === 0 ? 'FREE' : `₹ ${shipping}`}</span></div>
                                <div className="flex justify-between"><span>Tax (18%)</span><span>₹ {tax.toFixed(2)}</span></div>
                                <div className="border-t border-dashed border-gray-300 pt-3 mt-2"><div className="flex justify-between text-xl font-bold text-gray-900"><span>Amount Payable</span><span>₹ {total.toFixed(2)}</span></div></div>
                            </div>
                            <button onClick={handlePlaceOrder} disabled={isPlacingOrder} className={`w-full py-4 rounded-lg font-bold text-white shadow-lg transition-all flex justify-center items-center gap-2 ${isPlacingOrder ? 'bg-gray-400 cursor-not-allowed' : 'bg-orange-500 hover:bg-orange-600 transform hover:-translate-y-1'}`}>
                                {isPlacingOrder ? 'Processing...' : `PLACE ORDER • ₹ ${total.toFixed(0)}`}
                            </button>
                            <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500"><ShieldCheck className="w-4 h-4 text-green-600" /> Safe and Secure Payments.</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* --- COMPACT ADD/EDIT ADDRESS MODAL --- */}
            {showAddressModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-fadeIn">
                        
                        <div className="px-5 py-3 border-b border-gray-100 flex justify-between items-center bg-white">
                            <h3 className="text-lg font-bold text-gray-800">{editingId ? 'Edit Address' : 'Add New Address'}</h3>
                            <button onClick={() => setShowAddressModal(false)} className="text-gray-400 hover:text-red-500 transition-colors"><X className="w-5 h-5"/></button>
                        </div>
                        
                        <div className="p-5">
                            {!editingId && (
                                <button 
                                    onClick={handleCurrentLocation}
                                    disabled={locationLoading}
                                    className="w-full bg-blue-50 border border-blue-100 text-blue-600 text-sm font-bold py-2.5 rounded-lg flex items-center justify-center gap-2 mb-4 hover:bg-blue-100 transition-colors"
                                >
                                    {locationLoading ? <span className="animate-pulse">Detecting...</span> : <><Crosshair className="w-4 h-4" /> Use my current location</>}
                                </button>
                            )}

                            {!editingId && (
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="h-[1px] bg-gray-200 flex-1"></div>
                                    <span className="text-[10px] text-gray-400 font-bold uppercase">OR TYPE DETAILS</span>
                                    <div className="h-[1px] bg-gray-200 flex-1"></div>
                                </div>
                            )}

                            <form onSubmit={handleSaveAddress} className="space-y-3">
                                <div className="grid grid-cols-2 gap-3">
                                    <div className="relative">
                                        <User className="w-3.5 h-3.5 absolute top-3 left-3 text-gray-400" />
                                        <input type="text" placeholder="Name" required className="pl-9 border border-gray-300 p-2.5 rounded-lg w-full text-sm focus:border-teal-500 outline-none" value={newAddr.name} onChange={e => setNewAddr({...newAddr, name: e.target.value})} />
                                    </div>
                                    <div className="relative">
                                        <Phone className="w-3.5 h-3.5 absolute top-3 left-3 text-gray-400" />
                                        <input type="text" placeholder="Mobile" required maxLength={10} className="pl-9 border border-gray-300 p-2.5 rounded-lg w-full text-sm focus:border-teal-500 outline-none" value={newAddr.phone} onChange={e => setNewAddr({...newAddr, phone: e.target.value.replace(/\D/g,'')})} />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-3">
                                    <input type="text" placeholder="Pincode" required maxLength={6} className="border border-gray-300 p-2.5 rounded-lg w-full text-sm focus:border-teal-500 outline-none" value={newAddr.pincode} onChange={e => setNewAddr({...newAddr, pincode: e.target.value.replace(/\D/g,'')})} />
                                    <input type="text" placeholder="City" required className="border border-gray-300 p-2.5 rounded-lg w-full text-sm bg-gray-50" value={newAddr.city} onChange={e => setNewAddr({...newAddr, city: e.target.value})} />
                                </div>

                                <textarea placeholder="Flat, House no., Area, Street" required rows={2} className="border border-gray-300 p-2.5 rounded-lg w-full text-sm focus:border-teal-500 outline-none resize-none" value={newAddr.addressLine} onChange={e => setNewAddr({...newAddr, addressLine: e.target.value})}></textarea>
                                
                                <div className="grid grid-cols-2 gap-3 items-center">
                                    <input type="text" placeholder="State" required className="border border-gray-300 p-2.5 rounded-lg w-full text-sm bg-gray-50" value={newAddr.state} onChange={e => setNewAddr({...newAddr, state: e.target.value})} />
                                    <div className="flex gap-2">
                                        <button type="button" onClick={() => setNewAddr({...newAddr, type: 'HOME'})} className={`flex-1 py-2 rounded-md border text-xs font-bold flex items-center justify-center gap-1 ${newAddr.type === 'HOME' ? 'bg-teal-50 border-teal-500 text-teal-700' : 'border-gray-200 text-gray-500'}`}><Home className="w-3 h-3" /> Home</button>
                                        <button type="button" onClick={() => setNewAddr({...newAddr, type: 'WORK'})} className={`flex-1 py-2 rounded-md border text-xs font-bold flex items-center justify-center gap-1 ${newAddr.type === 'WORK' ? 'bg-teal-50 border-teal-500 text-teal-700' : 'border-gray-200 text-gray-500'}`}><Briefcase className="w-3 h-3" /> Work</button>
                                    </div>
                                </div>

                                <div className="flex gap-3 pt-3 border-t border-gray-100 mt-2">
                                    <button type="submit" className="flex-1 bg-teal-600 text-white py-2.5 rounded-lg font-bold text-sm hover:bg-teal-700 shadow-lg">{editingId ? 'UPDATE ADDRESS' : 'SAVE ADDRESS'}</button>
                                    <button type="button" onClick={() => setShowAddressModal(false)} className="px-4 py-2.5 rounded-lg font-bold text-sm text-gray-500 hover:bg-gray-100">CANCEL</button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default CheckoutPage;